/**
 * numberCitations(markdown, citations) — replace Manubot tokens
 *   `[@doi:10.x/y]` / `[@pmid:123]` / `[@tag:abcd1234]`
 * with sequential `[N]` and return an ordered references list.
 *
 * Numbering follows the order of FIRST appearance in the markdown body. Each
 * unique source gets a single number; subsequent occurrences reuse it. Tokens
 * that don't match any provided citation entry are left untouched (so the
 * user can spot hallucinated references in the rendered text).
 *
 * Returns:
 *   processedMarkdown — markdown with [N] replacing the Manubot tokens,
 *                       collapsed adjacent refs ([1], [2] → [1, 2]),
 *                       and a final '\n\n## References\n' block appended.
 *   references[]      — [{ n, manubot_ref, title, doi, pmid, doc_id }]
 */

const TOKEN_RE = /@(doi|pmid|tag):([A-Za-z0-9._/\-]+)/gi;
// Brackets that wrap one or more citation tokens, possibly separated by ; or , .
//   [@doi:xxx]                        — single
//   [@doi:xxx; @doi:yyy]              — semicolon-joined
//   [@doi:xxx, @pmid:111]             — comma-joined
const BRACKET_RE = /\[\s*(@(?:doi|pmid|tag):[A-Za-z0-9._/\-]+(?:[\s,;]+@(?:doi|pmid|tag):[A-Za-z0-9._/\-]+)*)\s*\]/gi;

export function numberCitations(markdown, citations = []) {
  if (!markdown) return { processedMarkdown: '', references: [] };

  // Index citation metadata by its various keys so we can quickly look up by
  // the inline token. `citations` shape (from /api/thesis/chapters): 
  // { manubot_ref, source_title, doi, pmid, doc_id, ... }
  const lookup = new Map();
  for (const c of citations) {
    if (c.doi) lookup.set(`doi:${c.doi.toLowerCase()}`, c);
    if (c.pmid) lookup.set(`pmid:${c.pmid}`, c);
    if (c.doc_id) {
      lookup.set(`tag:${c.doc_id}`, c);
      // 8-char prefix variant used by the AI
      lookup.set(`tag:${c.doc_id.slice(0, 8).toLowerCase()}`, c);
    }
    if (c.manubot_ref) {
      const ref = c.manubot_ref.replace(/^@/, '').toLowerCase();
      lookup.set(ref, c);
    }
  }

  const refsInOrder = [];
  const seen = new Map();

  function getOrAssign(kind, id) {
    const key = `${kind.toLowerCase()}:${kind.toLowerCase() === 'tag' ? id : id.toLowerCase()}`;
    if (seen.has(key)) return seen.get(key);
    const meta = lookup.get(key) || null;
    const n = refsInOrder.length + 1;
    const display = meta
      ? meta
      : { manubot_ref: `@${kind}:${id}`, source_title: '(source not in project library)', doi: kind === 'doi' ? id : '', pmid: kind === 'pmid' ? id : '', doc_id: kind === 'tag' ? id : '', unmatched: true };
    refsInOrder.push({ n, ...display });
    seen.set(key, n);
    return n;
  }

  // First pass: bracketed groups (possibly multi-token).
  let body = markdown.replace(BRACKET_RE, (_full, inner) => {
    const nums = [];
    let m;
    const re = new RegExp(TOKEN_RE.source, 'gi');
    while ((m = re.exec(inner)) !== null) {
      nums.push(getOrAssign(m[1], m[2]));
    }
    return nums.length ? `[${nums.join(', ')}]` : _full;
  });
  // Second pass: bare tokens (no surrounding brackets).
  body = body.replace(TOKEN_RE, (_match, kind, id) => {
    const n = getOrAssign(kind, id);
    return `[${n}]`;
  });

  // Collapse adjacent ref groups, e.g. `[1] [2]` → `[1, 2]`.
  body = body.replace(/\[([\d,\s]+)\]\s*[,;]?\s*\[([\d,\s]+)\]/g,
    (_m, a, b) => `[${a.trim()}, ${b.trim()}]`);
  body = body.replace(/\[([\d,\s]+)\]\s*[,;]?\s*\[([\d,\s]+)\]/g,
    (_m, a, b) => `[${a.trim()}, ${b.trim()}]`);

  // Strip the AI's "## References in this chapter" block.
  body = body.replace(/##\s*References?(?:\s+in\s+this\s+chapter)?[\s\S]*$/i, '').trimEnd();

  if (refsInOrder.length === 0) {
    return { processedMarkdown: body, references: [] };
  }

  const refsBlock = refsInOrder.map((r) => formatReference(r)).join('\n');
  const processedMarkdown = `${body}\n\n## References\n${refsBlock}\n`;
  return { processedMarkdown, references: refsInOrder };
}

function formatReference(r) {
  const title = r.source_title || '(untitled)';
  const trailing = [];
  if (r.doi) trailing.push(`[DOI:${r.doi}](https://doi.org/${r.doi})`);
  if (r.pmid) trailing.push(`[PubMed:${r.pmid}](https://pubmed.ncbi.nlm.nih.gov/${r.pmid}/)`);
  const suffix = trailing.length ? ` · ${trailing.join(' · ')}` : '';
  const flag = r.unmatched ? ' ⚠️ *unverified*' : '';
  return `${r.n}. ${title}${suffix}${flag}`;
}
